import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, lastValueFrom, map } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class BaseService {
  data!: string;
  env: string = environment.env;
  isShare: boolean = this.document.location.href.includes('share');
  baseApiUrl: string = this.getBaseApiUrl();

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document
  ) {}

  getBaseApiUrl() {
    return this.isShare ? environment.baseApiShareUrl : environment.baseApiUrl;
  }

  async get(url: string) {
    return await firstValueFrom(this.http.get(`${this.baseApiUrl}/${url}`));
  }

  async getBulk(urls: string[]) {
    const requests = urls.map((url) =>
      this.http.get(`${this.baseApiUrl}/${url}`)
    );
    return await lastValueFrom(
      forkJoin(requests).pipe(
        map((results) => results.filter((x: any) => !x.error))
      )
    );
  }

  async getCities() {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}.mobo-api.com.br/api/v1/City/associated`
      )
    );
  }

  async getCampaign(campaignId: string) {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}-catalog.mobo-api.com.br/v5/campaign/${campaignId}`
      )
    );
  }

  async getCampaigns(cityId: string) {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}-catalog.mobo-api.com.br/v3/campaign?cityId=${cityId}`
      )
    );
  }

  async getCategories() {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}-catalog.mobo-api.com.br/v1/category/all`
      )
    );
  }

  async getCampaignsByCategory(cityId: string, categoryId: string) {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}-catalog.mobo-api.com.br/v3/campaign?cityId=${cityId}&categorieId=${categoryId}`
      )
    );
  }

  async getRestaurant(restaurantId: string) {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}-catalog.mobo-api.com.br/v2/commercialPlace?commercialPlaceId=${restaurantId}`
      )
    );
  }

  async getRestaurantBySlug(restaurantSlug: string) {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}-catalog.mobo-api.com.br/v3/commercialPlace/getbySlug?slug=${restaurantSlug}`
      )
    );
  }

  async getRestaurants(cityId: string) {
    return await firstValueFrom(
      this.http.get(
        `https://${this.env}-catalog.mobo-api.com.br/v2/commercialPlace?cityId=${cityId}`
      )
    );
  }

  async getFAQ() {
    return await firstValueFrom(
      this.http.get(`https://${this.env}.mobo-api.com.br/api/v1/FAQ/all`)
    );
  }

  async getEvents() {
    const observable = this.http.get(
        `https://${this.env}.mobo-api.com.br/api/v2/Campaign/banners`
    ).pipe(
      map(response => {
        return this.toPascalCaseKeys(response);
      })
    );
    return await firstValueFrom(observable);
  }

  async getCampaignsByEvent(campaignEventId: string) {
    const observable = this.http.get(
      `https://${this.env}.mobo-api.com.br/api/v1/Campaign/event/${campaignEventId}/campaigns`
    ).pipe(
      map(response => {
        return this.toPascalCaseKeys(response);
      })
    );
    return await firstValueFrom(observable);
  }

  async getPascalCase(url: string) {
    const observable = this.http.get(`${this.baseApiUrl}/${url}`).pipe(
      map(response => {
        return this.toPascalCaseKeys(response);
      })
    );
    return await firstValueFrom(observable);
  }

  toPascalCaseKeys(obj: any): any { // TODO: remover assim que os endpoints irem pro catalog
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.toPascalCaseKeys(item));
    }

    return Object.keys(obj).reduce((accumulator, key) => {
      const newKey = key.charAt(0).toUpperCase() + key.slice(1);
      accumulator[newKey] = this.toPascalCaseKeys(obj[key]);
      return accumulator;
    }, {} as any);
  }
}
